import { useEffect } from "react";
import { motion } from "framer-motion";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { screenTime } from "../../redux/ApiSlice/AccountSettingSlice";
import { Heart, MessageCircle, Clock, Trash, CircleUser, UserRoundX } from "lucide-react";

const YourActivity = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { screenTimeData, screenTimeLoading, screenTimeError } = useSelector((state) => state.AccountSetting);

    useEffect(() => {
        dispatch(screenTime());
    }, [dispatch]);

    const menuList = [
        { name: "Likes", icon: <Heart size={16} />, path: "/settings/liked-videos" },
        { name: "Comments", icon: <MessageCircle size={15} />, path: "/settings/comments" },
        { name: "Watch History", icon: <Clock size={16} />, path: "/settings/watch-history" },
        { name: "Recently Deleted", icon: <Trash size={16} />, path: "/settings/recently-deleted" },
        { name: "Account History", icon: <CircleUser size={16} />, path: "/settings/account-history" },
        { name: "Blocked Accounts", icon: <UserRoundX size={16} />, path: "/settings/blocked" },
    ];

    const BAR_MAX_HEIGHT = 96; // matches h-24 utility

    // Example: Change to 24 for 24-hour scale, or 12 for 12-hour scale
    const DAY_IN_MINUTES = 6 * 60;

    const parseTimeToMinutes = (timeStr = "") => {
        const normalized = String(timeStr).trim().toLowerCase().replace(/\s+/g, '');
        if (!normalized) return 0;

        if (normalized.includes(":")) {
            const [hoursStr = "0", minutesStr = "0"] = normalized.split(":");
            const hours = Number(hoursStr) || 0;
            // Strip any non-numeric characters (like 'm') from minutes
            const minutesClean = minutesStr.replace(/[^0-9.]/g, '');
            const minutes = Number(minutesClean) || 0;
            return Math.max(hours, 0) * 60 + Math.max(minutes, 0);
        }

        if (normalized.includes("hr")) {
            const hours = parseFloat(normalized) || 0;
            return Math.max(hours, 0) * 60;
        }

        if (normalized.includes("m")) {
            const minutes = parseFloat(normalized) || 0;
            return Math.max(minutes, 0);
        }

        return 0;
    };

    const calculateHeight = (timeStr) => {
        const minutes = parseTimeToMinutes(timeStr);
        const clampedMinutes = Math.min(minutes, DAY_IN_MINUTES);
        return (clampedMinutes / DAY_IN_MINUTES) * BAR_MAX_HEIGHT;
    };

    const weeklyData = (screenTimeData?.weekly_data || [])
        .map((item) => {
            const time = item.formatted_time || item.time || "0:00";
            const day = (item.label || item.day || "").toUpperCase();
            return {
                day,
                displayTime: time,
                height: calculateHeight(time),
            };
        });

    const rawAverage = screenTimeData?.average_formatted;
    const hasAverage = typeof rawAverage === "string" && rawAverage.trim() !== "";
    const averageLabel = screenTimeLoading ? "Loading..." : hasAverage ? rawAverage : "N/A";

    return (
        <div className="h-full bg-white dark:bg-[#1E1D21]">
            <div className="sticky top-0 z-50">
                <div className="relative bg-[#BDE6FF] rounded-b-xl h-12 flex items-center justify-center">
                    <IoMdArrowBack
                        onClick={() => navigate("/account/settings")}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[22px] text-black dark:text-black cursor-pointer"
                    />
                    <p className="font-bold text-[16px] text-black dark:text-black">Your Activity</p>
                </div>
            </div>

            {/* Menu List */}
            {menuList.map((item, index) => (
                <div
                    key={index}
                    onClick={() => item.path && navigate(`${item.path}`)}
                    className="flex items-center px-3 py-3 space-x-2 border-b border-gray-300/50 dark:border-gray-800/50 text-black dark:text-white cursor-pointer"
                >
                    <span>{item.icon}</span>
                    <p className="font-medium text-[14px]">{item.name}</p>
                </div>
            ))}

            {/* Activity History */}
            <div className="px-3 py-4">
                <p className="font-semibold text-[14px] bg-gray-300 dark:bg-[#333333] text-black dark:text-white px-2 py-1 rounded-md">
                    Screen Time Average: {averageLabel}
                </p>
                {screenTimeError && (
                    <p className="mt-2 text-xs text-red-600 dark:text-red-400">Unable to load latest screen time.</p>
                )}

                {/* Week Progress Bar */}
                {weeklyData.length === 0 ? (
                    <p className="mt-6 text-xs text-gray-600 dark:text-gray-300">No screen time data available.</p>
                ) : (
                    <div className="mt-10 flex items-end justify-evenly space-x-2">
                        {weeklyData.map((item, index) => (
                            <div key={index} className="flex flex-col items-center">
                                <span className="text-xs font-semibold mb-1 text-black dark:text-white">{item.displayTime}</span>
                                <div className="w-8 h-24 bg-gray-300 dark:bg-[#333333] rounded-md relative flex items-end overflow-hidden">
                                    <motion.div
                                        className="w-full bg-[#BDE6FF] rounded-md"
                                        initial={{ height: 0 }}
                                        animate={{ height: `${item.height}px` }}
                                        transition={{
                                            duration: 0.8,
                                            delay: index * 0.1,
                                            ease: "easeOut"
                                        }}
                                    ></motion.div>
                                </div>
                                <span className="text-xs font-semibold mt-0.5 text-black dark:text-white">{item.day}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
export default YourActivity